"use client"

import * as React from "react"
import { useTranslations } from "next-intl"
import { useParams, useRouter } from "next/navigation"
import { useQuery } from "@tanstack/react-query"
import { HttpTypes } from "@medusajs/types"
import { Search } from "@modules/common/icons/search"
import X from "@modules/common/icons/x"
import Spinner from "@modules/common/icons/spinner"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useDebouncedSearch } from "@modules/search/hooks/use-debounced-search"
import { searchQueryOptions } from "@lib/queries/products"
import { SearchAutocomplete } from "@modules/search/components/search-autocomplete"
import { cn } from "@lib/utils"

interface SearchBarProps {
  region: HttpTypes.StoreRegion
  className?: string
  showAutocomplete?: boolean
  onSearch?: (query: string) => void
}

/**
 * SearchBar component - search input with autocomplete
 * Features:
 * - Debounced search (300ms) powered by React Query
 * - Autocomplete dropdown with product previews
 * - Keyboard navigation (Arrow keys, Enter, Esc)
 * - Clear button
 * - Loading indicator
 */
export function SearchBar({
  region,
  className,
  showAutocomplete = true,
  onSearch,
}: SearchBarProps) {
  const t = useTranslations("common")
  const router = useRouter()
  const params = useParams()
  const countryCode = params.countryCode as string

  const [query, setQuery] = React.useState("")
  const [isAutocompleteOpen, setIsAutocompleteOpen] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(-1)

  const debouncedQuery = useDebouncedSearch(query, 300)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Use React Query for search - enabled only when debounced query >= 2 chars
  const { data, isLoading, isFetching } = useQuery(
    searchQueryOptions({
      query: debouncedQuery,
      countryCode,
      limit: 8, // Limit autocomplete to 8 results
    })
  )

  const products = data?.products ?? []
  const isSearching = (isLoading || isFetching) && debouncedQuery.length >= 2

  // Open autocomplete when products arrive
  React.useEffect(() => {
    if (debouncedQuery.length >= 2 && products.length > 0 && showAutocomplete) {
      setIsAutocompleteOpen(true)
    } else if (debouncedQuery.length < 2) {
      setIsAutocompleteOpen(false)
    }
  }, [debouncedQuery, products.length, showAutocomplete])

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    setSelectedIndex(-1)
  }

  // Handle clear button
  const handleClear = () => {
    setQuery("")
    setIsAutocompleteOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  // Handle search submit (Enter key or form submit)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (selectedIndex >= 0 && products[selectedIndex]) {
      // Navigate to selected product
      const product = products[selectedIndex]
      router.push(`/${countryCode}/products/${product.handle}`)
      setIsAutocompleteOpen(false)
      setQuery("")
    } else if (query.trim().length > 0) {
      // Navigate to search results page
      router.push(`/${countryCode}/search?q=${encodeURIComponent(query)}`)
      setIsAutocompleteOpen(false)
      if (onSearch) {
        onSearch(query)
      }
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isAutocompleteOpen || products.length === 0) {
      if (e.key === "Escape") {
        handleClear()
      }
      return
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < products.length - 1 ? prev + 1 : prev
        )
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case "Escape":
        e.preventDefault()
        setIsAutocompleteOpen(false)
        setSelectedIndex(-1)
        break
      case "Enter":
        e.preventDefault()
        handleSubmit(e)
        break
    }
  }

  // Handle product click in autocomplete
  const handleProductClick = () => {
    setIsAutocompleteOpen(false)
    setQuery("")
  }

  return (
    <div className={cn("relative w-full", className)}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center">
          <Search
            size="20"
            className="absolute left-3 text-ui-fg-subtle pointer-events-none z-10"
          />
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t("search.placeholder")}
            className="w-full pl-10 pr-10"
            autoComplete="off"
            role="combobox"
            aria-expanded={products.length > 0 && isAutocompleteOpen}
            aria-controls="search-autocomplete-listbox"
            aria-autocomplete="list"
            aria-activedescendant={selectedIndex >= 0 ? `search-option-${selectedIndex}` : undefined}
          />
          {isSearching && (
            <Spinner
              size="16"
              className="absolute right-10 text-ui-fg-subtle animate-spin"
            />
          )}
          {query && !isSearching && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="absolute right-1 h-7 w-7 text-ui-fg-subtle hover:text-ui-fg-base"
              aria-label={t("search.clearSearch")}
            >
              <X size="16" />
            </Button>
          )}
        </div>
      </form>

      {showAutocomplete && (
        <SearchAutocomplete
          products={products}
          query={query}
          isLoading={isSearching && debouncedQuery.length > 0}
          isOpen={isAutocompleteOpen}
          onClose={() => setIsAutocompleteOpen(false)}
          onProductClick={handleProductClick}
          selectedIndex={selectedIndex}
          region={region}
        />
      )}
    </div>
  )
}
